import type { NodeCategory } from '@hyphen-db-studio/architecture-core';
import { Boxes, ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  catalogByCategory,
  NODE_CATEGORIES,
  NODE_CATEGORY_LABELS,
  type CatalogEntry,
} from '../../lib/architectureNodeCatalog';
import { useArchitectureStore } from '../../store/useArchitectureStore';

const DEFAULT_OPEN_CATEGORIES = new Set<NodeCategory>(['database']);

interface SubGroup {
  subCategory: string;
  entries: CatalogEntry[];
}

interface CategoryGroup {
  category: NodeCategory;
  ungrouped: CatalogEntry[];
  subGroups: SubGroup[];
  total: number;
}

function NodeRow({ entry, addNode }: { entry: CatalogEntry; addNode: ReturnType<typeof useArchitectureStore.getState>['addNode'] }) {
  const Icon = entry.icon.icon;
  return (
    <div className="group flex items-center gap-0.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
      <button
        onClick={() => addNode(entry.kind, entry.category, entry.label)}
        title={`Add ${entry.label}`}
        className="flex flex-1 items-center gap-2 truncate rounded px-1.5 py-1 text-left text-sm text-gray-700 dark:text-gray-300"
      >
        <Icon className="h-4 w-4 shrink-0" style={{ color: entry.color }} />
        <span className="truncate">{entry.label}</span>
      </button>
      <button
        onClick={() => addNode(entry.kind, entry.category, entry.label, 'group')}
        title={`Add ${entry.label} as a group (wraps other nodes)`}
        className="shrink-0 rounded p-1 text-gray-400 opacity-0 hover:bg-gray-200 hover:text-gray-700 group-hover:opacity-100 dark:hover:bg-gray-700 dark:hover:text-gray-200"
      >
        <Boxes className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function ArchitecturePalette({ width }: { width: number }) {
  const addNode = useArchitectureStore((s) => s.addNode);
  const [filter, setFilter] = useState('');
  const [openCategories, setOpenCategories] = useState<Set<NodeCategory>>(() => new Set(DEFAULT_OPEN_CATEGORIES));
  const [openSubCategories, setOpenSubCategories] = useState<Set<string>>(() => new Set());

  const groups: CategoryGroup[] = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return NODE_CATEGORIES.map((category) => {
      const entries = catalogByCategory(category).filter((entry) => !term || entry.label.toLowerCase().includes(term));
      const ungrouped: CatalogEntry[] = [];
      const subMap = new Map<string, CatalogEntry[]>();
      for (const entry of entries) {
        if (entry.subCategory) {
          const list = subMap.get(entry.subCategory) ?? [];
          list.push(entry);
          subMap.set(entry.subCategory, list);
        } else {
          ungrouped.push(entry);
        }
      }
      const subGroups = Array.from(subMap.entries()).map(([subCategory, list]) => ({ subCategory, entries: list }));
      return { category, ungrouped, subGroups, total: entries.length };
    }).filter((g) => g.total > 0);
  }, [filter]);

  const isFiltering = filter.trim().length > 0;

  function toggleCategory(category: NodeCategory) {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  function toggleSubCategory(key: string) {
    setOpenSubCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div
      style={{ width }}
      className="flex h-full shrink-0 flex-col border-r border-gray-200/70 bg-panel dark:border-white/10 dark:bg-gray-900"
    >
      <div className="border-b border-gray-200/70 p-2 dark:border-white/10">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter nodes..."
          className="w-full rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-sky-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
        />
      </div>
      <div className="flex-1 overflow-y-auto px-1 py-2">
        {groups.map(({ category, ungrouped, subGroups, total }) => {
          const open = isFiltering || openCategories.has(category);
          return (
            <div key={category} className="mb-0.5">
              <button
                onClick={() => toggleCategory(category)}
                className="flex w-full items-center gap-1 rounded px-1.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                {open ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
                <span className="truncate">{NODE_CATEGORY_LABELS[category]}</span>
                <span className="ml-auto shrink-0 text-[10px] font-normal normal-case text-gray-400 dark:text-gray-500">
                  {total}
                </span>
              </button>

              {open && (
                <div className="ml-2.5 space-y-0.5 border-l border-gray-200 pl-2 dark:border-gray-700">
                  {subGroups.map(({ subCategory, entries }) => {
                    const key = `${category}::${subCategory}`;
                    const subOpen = isFiltering || openSubCategories.has(key);
                    return (
                      <div key={key}>
                        <button
                          onClick={() => toggleSubCategory(key)}
                          className="flex w-full items-center gap-1 rounded px-1.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        >
                          {subOpen ? <ChevronDown className="h-2.5 w-2.5 shrink-0" /> : <ChevronRight className="h-2.5 w-2.5 shrink-0" />}
                          <span className="truncate">{subCategory}</span>
                          <span className="ml-auto shrink-0 text-[10px] font-normal normal-case text-gray-400 dark:text-gray-500">
                            {entries.length}
                          </span>
                        </button>
                        {subOpen && (
                          <div className="ml-2 space-y-0.5 border-l border-gray-100 pl-2 dark:border-gray-800">
                            {entries.map((entry) => (
                              <NodeRow key={entry.kind} entry={entry} addNode={addNode} />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {ungrouped.map((entry) => (
                    <NodeRow key={entry.kind} entry={entry} addNode={addNode} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
